// Functions to work with facebook's pixel API
import { defineFbqFunctionGlobally } from './fbPixelPrepare';
import { consoleLogger, environmentService } from '../config/di/container';

export const isFBPixelLoaded = (): boolean => {
  return !!window.fbq;
};

/**
 * Loads facebook's pixel script into the current DOM.
 * Returns a promise which resolves to {@code true} if the script needs to be initialized after.
 */
export function loadFBPixel(fbPixelId?: string): Promise<boolean> {
  if (!fbPixelId) {
    // don't try to load it if no fbPixelId got provided
    consoleLogger.error('Failed to load pixel', {
      pixel: 'facebook',
      reason: 'No id provided!'
    });
    return Promise.resolve(false);
  }

  if (isFBPixelLoaded()) {
    consoleLogger.debug('⚙️ Pixel already attached to DOM', {
      pixel: 'facebook',
      id: fbPixelId
    });
    return Promise.resolve(false);
  }

  return new Promise((resolve, reject) => {
    defineFbqFunctionGlobally();

    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.type = 'text/plain';
    script.async = true;
    script.setAttribute('data-cookieconsent', 'marketing');
    script.onerror = function () {
      reject('Failed to load fb pixel script');
    };
    document.head.appendChild(script);
    resolve(true);

    consoleLogger.debug('⚙️ Pixel added to DOM', {
      pixel: 'facebook',
      id: fbPixelId
    });
  });
}

/**
 * Init facebook's pixel script.
 * Returns a promise which resolves to {@code true} if fb pixel is ready to use.
 */
export function initFBPixel(initNeeded: boolean, fbPixelId?: string): Promise<boolean> {
  if (!initNeeded) {
    return Promise.resolve(isFBPixelLoaded());
  }

  return new Promise((resolve) => {
    // https://developers.facebook.com/docs/facebook-pixel/implementation/tag_spa
    // Facebook's script listens on history api changes by default.
    // We deactivate it to have it aligned with our already existing "path change"-based
    // page view tracking.
    // @ts-ignore
    fbq.disablePushState = true;

    fbq('init', fbPixelId!);

    resolve(true);
  });
}

export const trackPage = () => {
  if (!environmentService.getFbPixelId()) {
    consoleLogger.debug('⚠️ FB_PIXEL_ID is not set. trackPage was triggered');
  }
  if (!isFBPixelLoaded()) {
    return;
  }

  // assumption: fbq handles URLs and paths on its own based on current history api state
  window.fbq('track', 'PageView');
};

export const trackGenericEvent = (
  pageName: string,
  action: string,
  category: string,
  label?: string,
  value?: string
) => {
  if (!environmentService.getFbPixelId()) {
    consoleLogger.debug('⚠️ FB_PIXEL_ID is not set. trackEvent was triggered', {
      action,
      category,
      label,
      value
    });
  }
  if (!isFBPixelLoaded()) {
    return;
  }

  // @ts-ignore
  window.fbq('trackCustom', 'generic', {
    // @ts-ignore
    pageName,
    // @ts-ignore
    action,
    category,
    label,
    // @ts-ignore
    value
  });
};
